import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
} from "@mui/material";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";
import ToastManager from "../../Components/ToastManager";
import { NetworkManager } from "../../Components/NetworkManager";
import { CommonUtil } from "../../utils/CommonUtil";

export interface Campaign {
  messageCampaignId: any;
  messageCampaignName: string;
  lifetimeBudget: number;
  campaignStatus: string;
}

interface CampaignDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (newCampaign: Campaign) => void;
  editCampaign: any;
  fetchCampaigns: () => void;
}

interface CampaignFormValues {
  messageCampaignName: string;
  lifetimeBudget: number;
  campaignStatus: string;
}

export const NewCampaignDialog: React.FC<CampaignDialogProps> = ({
  open,
  onClose,
  onSave,
  editCampaign,
  fetchCampaigns,
}) => {
  const initialValues: CampaignFormValues = {
    messageCampaignName: editCampaign?.messageCampaignName || "",
    lifetimeBudget: editCampaign?.lifetimeBudget || 0,
    campaignStatus: editCampaign?.campaignStatus ?? false,
  };

  const validationSchema = Yup.object().shape({
    messageCampaignName: Yup.string().required("Campaign Name is required"),
    lifetimeBudget: Yup.number()
      .required("Lifetime budget is required")
      .positive(),
    campaignStatus: Yup.boolean(),
  });

  const { adAccountId }: any = JSON.parse(
    localStorage.getItem("fbbAdAccountDetail") || "{}",
  );
  const {
    facebook: { pageId, systemUserId },
  }: any = JSON.parse(localStorage.getItem("fbbPageDetail") || "{}");

  const handleSubmit = async (
    values: CampaignFormValues,
    formikHelpers: FormikHelpers<CampaignFormValues>,
    onSave: (campaign: Campaign) => void,
    onClose: () => void,
  ) => {
    const payload = {
      messageCampaignName: values.messageCampaignName,
      adAccountId,
      systemUserId,
      lifetimeBudget: values.lifetimeBudget * 100,
      campaignStatus: values.campaignStatus ? "active" : "inactive",
      pageId,
    };

    try {
      NetworkManager.sendJsonRequest(
        "broadcastMessageTemplates/createMessageCampaign",
        payload,
        "",
        "POST",
        true,
        (data: any) => {
          const respData = CommonUtil.parseData(data);
          if (respData?.statusCode === "BM0000") {
            const createdCampaign: Campaign = {
              messageCampaignId:
                respData?.messageCampaignId || crypto.randomUUID(),
              messageCampaignName: values.messageCampaignName,
              lifetimeBudget: values.lifetimeBudget,
              campaignStatus: values.campaignStatus,
            };
            onSave(createdCampaign);
            ToastManager.showToast(
              "New Campaign created successfully.",
              "Success",
            );
            formikHelpers.resetForm();
            onClose();
            fetchCampaigns();
          } else if (respData?.statusCode === "BM0001") {
            ToastManager.showToast(
              "Name is already taken. Please choose another one.",
              "Error",
            );
          } else if (respData?.statusCode === "BM0002") {
            ToastManager.showToast(
              `Budget is too low, ${respData?.ErrorDetails?.error?.error_user_msg}`,
              "Error",
            );
          }
        },
        (error: any) => {
          ToastManager.showToast("Error while creating campaign.", "Error");
        },
      );
    } catch (error) {
      ToastManager.showToast("Unexpected error occurred.", "Error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onClose();
        }
      }}
      fullWidth
      slotProps={{
        paper: { sx: { maxHeight: "100%", maxWidth: "40%" } },
      }}>
      <DialogTitle sx={{ textAlign: "center" }}>
        {editCampaign ? "Edit Campaign" : "Create Campaign"}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(values, helpers) =>
          handleSubmit(values, helpers, onSave, onClose)
        }>
        {({ handleChange, values, setFieldValue }) => (
          <Form>
            <DialogContent
              className="custom-scrollbar-css"
              sx={{
                pt: 2,
                maxHeight: "410px",
                overflowY: "auto",
              }}>
              <Box>
                <TextField
                  fullWidth
                  required
                  name="messageCampaignName"
                  label="Campaign Name"
                  value={values.messageCampaignName}
                  onChange={handleChange}
                  sx={{ mt: 1 }}
                />
                <FormControlLabel
                  label={values.campaignStatus ? "Enabled" : "Disabled"}
                  sx={{ mt: 2 }}
                  control={
                    <Switch
                      checked={values.campaignStatus === "active"}
                      onChange={(e) =>
                        setFieldValue("campaignStatus", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                />
                <TextField
                  fullWidth
                  required
                  name="lifetimeBudget"
                  label="Lifetime Budget ($)"
                  type="number"
                  value={values.lifetimeBudget}
                  onChange={handleChange}
                  sx={{ mt: 3 }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between", p: 3 }}>
              <Button onClick={onClose} color="primary">
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {editCampaign ? "Update" : "Save"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
